import React from "react"
import PropTypes from "prop-types"
import { Box, ButtonBase } from "@mui/material"
import InfoDataPanel from "src/v2/components/InfoDataPanel/info-data-panel"
import { formatDateString } from "src/utils/format-date"
import { formatCpfCnpj } from "src/v2/utils/formatter/format-cpf-cnpj"

const formatDate = (date) => formatDateString(date, "-")
const getCompanySelectLabel = (company) => (
    `Selecionar ${company?.RazaoSocial || formatCpfCnpj(company?.CPFCNPJ) || "empresa"}`
)
const gridColumns = { xxxs: 12, xs: 12, md: 12, lg: 12 }
const headerItemSx = {
    flex: {
        xxxs: "0 0 50%",
        md: "0 0 16.6667%",
    },
    width: {
        xxxs: "50%",
        md: "16.6667%",
    },
    maxWidth: {
        xxxs: "50%",
        md: "16.6667%",
    },
    minWidth: 0,
}

const headerSections = [
    {
        sx: {
            columns: gridColumns,
            py: { xxxs: 2, md: 3 },
            px: 0,
            alignItems: { md: "center" },
            flexDirection: "row",
            background: "transparent",
        },
        items: [
            {
                key: "GrupoEconomico_Nome",
                label: "Líder do Grupo Econômico",
                gridProps: { xxxs: 6, xs: 6, md: 2, lg: 2 },
                sx: headerItemSx,
            },
            {
                key: "GrupoEconomico_RaizCNPJ",
                label: "Raiz CNPJ",
                gridProps: { xxxs: 6, xs: 6, md: 2, lg: 2 },
                sx: headerItemSx,
            },
        ],
    },
]

const mainGridProps = { xxxs: 6, xs: 6, md: 3.0833, lg: 2.4 }
const validityGridProps = { xxxs: 6, xs: 6, md: 1.375, lg: 1.35 }

const noWrapLabelSx = {
    "& .MuiTypography-root:first-of-type": {
        whiteSpace: "nowrap",
        overflowWrap: "normal",
        wordBreak: "normal",
    },
}

const getCompanySections = (isAdminUser) => {
    const scoreGridProps = isAdminUser
        ? { xxxs: 6, xs: 6, md: 1.375, lg: 0.95 }
        : { xxxs: 12, xs: 12, md: 2.75, lg: 1.2 }

    const moodysGridProps = {
        xxxs: 4,
        xs: 4,
        md: 0.9167,
        lg: isAdminUser ? 0.9 : 1.2,
    }

    const fitchGridProps = {
        xxxs: 4,
        xs: 4,
        md: 0.9167,
        lg: isAdminUser ? 0.8 : 1.2,
    }

    const spGridProps = {
        xxxs: 4,
        xs: 4,
        md: 0.9167,
        lg: isAdminUser ? 0.8 : 1.2,
    }

    return [
        {
            columns: gridColumns,
            sx: {
                position: "relative",
                flexDirection: "row",
                p: { xxxs: 2, xs: 3, md: 4.5, lg: 3 },
                background: (theme) =>
                    `linear-gradient(to bottom right, ${theme.palette.transparent.white10} -40%, transparent 100%)`,
                "&::after": {
                    content: '""',
                    position: "absolute",
                    top: { md: 36, lg: 24 },
                    bottom: { md: 36, lg: 24 },
                    left: {
                        md: "calc(77.08% - 20px)",
                        lg: "calc(55% - 5px)",
                    },
                    width: "1px",
                    bgcolor: "common.white",
                    opacity: 0.8,
                    display: { xxxs: "none", md: "block" },
                },
            },
            items: [
                {
                    key: "RazaoSocial",
                    label: "Razão Social",
                    gridProps: mainGridProps,
                    sx: {
                        order: { xxxs: 0, md: 0, lg: 0 },
                        pb: { xxxs: 3, md: 4, lg: 3 },
                        pr: 2,
                    },
                },
                {
                    key: "NomeFantasia",
                    label: "Nome Fantasia",
                    gridProps: mainGridProps,
                    sx: {
                        order: { xxxs: 1, md: 1, lg: 1 },
                        pb: { xxxs: 3, md: 4, lg: 3 },
                        pr: 2,
                    },
                },
                {
                    key: "CPFCNPJ",
                    label: "CNPJ",
                    formatter: formatCpfCnpj,
                    gridProps: mainGridProps,
                    sx: {
                        order: { xxxs: 2, md: 2, lg: 2 },
                        pb: { xxxs: 3, md: 4, lg: 3 },
                        pr: 2,
                    },
                },
                {
                    key: "Rating_C2P",
                    label: "Score C2P",
                    gridProps: scoreGridProps,
                    sx: {
                        position: "relative",
                        order: { xxxs: 6, md: 3, lg: 3 },
                        pt: { xxxs: 3, md: 0 },
                        pb: { xxxs: 3, md: 4, lg: 3 },
                        pl: { md: 3 },
                        ...noWrapLabelSx,
                        "&::before": {
                            content: '""',
                            position: "absolute",
                            top: 0,
                            left: 0,
                            width: "200%",
                            height: "1px",
                            bgcolor: "common.white",
                            opacity: 0.8,
                            pointerEvents: "none",
                            display: { xxxs: "block", md: "none" },
                        },
                    },
                },
                ...(isAdminUser
                    ? [
                        {
                            key: "DataValidade_C2P",
                            label: "Validade C2P",
                            formatter: formatDate,
                            gridProps: validityGridProps,
                            sx: {
                                order: { xxxs: 7, md: 4, lg: 4 },
                                pt: { xxxs: 3, md: 0 },
                                pb: { xxxs: 3, md: 4, lg: 3 },
                                pl: 1,
                                ...noWrapLabelSx,
                            },
                        },
                    ]
                    : []),
                {
                    key: "TipoCadastro",
                    label: "Tipo Cadastro",
                    gridProps: mainGridProps,
                    sx: {
                        order: { xxxs: 3, md: 5, lg: 8 },
                        pb: { xxxs: 3, md: 0 },
                        pr: 2,
                    },
                },
                {
                    key: "Setor",
                    label: "Setor",
                    gridProps: mainGridProps,
                    sx: {
                        order: { xxxs: 4, md: 6, lg: 9 },
                        pb: { xxxs: 3, md: 0 },
                        pr: 2,
                    },
                },
                {
                    key: "SubSetor",
                    label: "Subsetor",
                    gridProps: mainGridProps,
                    sx: {
                        order: { xxxs: 5, md: 7, lg: 10 },
                        pb: { xxxs: 3, md: 0 },
                        pr: 2,
                    },
                },
                {
                    key: "Rating_Moodys",
                    label: "Moody’s",
                    gridProps: moodysGridProps,
                    sx: {
                        order: { xxxs: 8, md: 8, lg: 5 },
                        pb: { xxxs: 3, md: 0 },
                        pl: { md: 3, lg: 1 },
                        ...noWrapLabelSx,
                    },
                },
                {
                    key: "Rating_Fitch",
                    label: "Fitch",
                    gridProps: fitchGridProps,
                    sx: {
                        order: { xxxs: 8, md: 8, lg: 5 },
                        pb: { xxxs: 3, md: 0 },
                        pl: { md: 3, lg: 1 },
                        ...noWrapLabelSx,
                    },
                },
                {
                    key: "Rating_SP",
                    label: "S&P",
                    gridProps: spGridProps,
                    sx: {
                        order: { xxxs: 10, md: 10, lg: 7 },
                        pl: 1,
                        ...noWrapLabelSx,
                    },
                },
            ],
        },
    ]
}

export const EconomicGroupPanel = ({ data, loading, isAdminUser, onCompanySelect }) => {
    const groupData = data?.[0] ?? {}
    const companies = loading ? [{}, {}] : data
    const companySections = getCompanySections(isAdminUser)

    const handleCompanySelect = (company) => {
        if (!loading && company?.CPFCNPJ) {
            onCompanySelect(company.CPFCNPJ)
        }
    }

    return (
        <Box
            sx={{
                display: "flex",
                flexDirection: "column",
                width: {
                    xxxs: "calc(100% - 32px)",
                    md: "calc(100% - 48px)",
                },
                mx: "auto",
                pb: { xxxs: 2, md: 3 },
            }}
        >
            <InfoDataPanel
                data={groupData}
                sections={headerSections}
                loading={loading}
            />

            <Box display="flex" flexDirection="column" gap={"0.5px"}>
                {companies.map((company, index) => (
                    <ButtonBase
                        key={loading ? `loading-${index}` : company.CPFCNPJ}
                        component="div"
                        disabled={loading}
                        onClick={() => handleCompanySelect(company)}
                        aria-label={getCompanySelectLabel(company)}
                        sx={{
                            display: "block",
                            width: "100%",
                            textAlign: "left",
                            "&:hover": {
                                filter: loading ? "none" : "brightness(1.04)",
                            },
                            "&:focus-visible": {
                                outline: "2px solid",
                                outlineColor: "primary.main",
                                outlineOffset: "2px",
                            },
                        }}
                    >
                        <InfoDataPanel data={company} sections={companySections} loading={loading} />
                    </ButtonBase>
                ))}
            </Box>
        </Box>
    )
}

EconomicGroupPanel.propTypes = {
    data: PropTypes.arrayOf(PropTypes.object),
    loading: PropTypes.bool,
    isAdminUser: PropTypes.bool,
    onCompanySelect: PropTypes.func,
}

EconomicGroupPanel.defaultProps = {
    data: [],
    loading: false,
    isAdminUser: false,
    onCompanySelect: () => {},
}
